import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { Multiselect, DropdownList } from 'react-widgets';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import config from '../../config';
import { useI18n } from 'utils/I18n/context';

const initPage = config.initialPage;
const minLength = 3;
const defaultCurrency = 'EUR';
const defaultValue = 'all';

const templateKeys = {
    datepicker: 'getDatepicker',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull',
};

const DataComponentFilterFinanceBlock = ({ fields, onAction, startDate, endDate }) => {
    const { t } = useI18n();
    const location = useLocation();

    const getDefValues = useCallback((fields = []) => {
        const values = {};
        fields.map((item) => {
            if (!item.value) return;
            if (item.name === 'datepicker') {
                if (item.value.startDate) values[`${item.key}StartDate`] = item.value.startDate;
                if (item.value.endDate) values[`${item.key}EndDate`] = item.value.endDate;
            } else {
                values[item.key] = item.value;
            }
        });
        return values;
    }, []);

    const getSelected = useCallback((fields = [], values = {}) => {
        const selected = [];
        fields.map((item, i) => {
            if ((item.name === 'datepicker' && values[`${item.key}StartDate`]) || values[item.key]) {
                selected.push({ ...item, fieldIndex: i });
            }
        });
        return selected;
    }, []);

    const parsedQuery = query.parse(location.search || '');

    if (parsedQuery.startDate && typeof parsedQuery.startDate === 'string') {
        parsedQuery.startDate = moment(parsedQuery.startDate);
    }
    if (parsedQuery.endDate && typeof parsedQuery.endDate === 'string') {
        parsedQuery.endDate = moment(parsedQuery.endDate);
    }

    let initialValues = {
        page: parsedQuery.page || initPage,
        ...getDefValues(fields),
        ...parsedQuery,
    };

    if ('/office/finances/report' !== location.pathname) {
        initialValues.createStartDate =
            parsedQuery.startDate || startDate || moment().subtract(config.defaultDatepickerDaysPeriod, 'days');
        initialValues.createEndDate = parsedQuery.endDate || endDate || moment().endOf('day');
        initialValues.statuses = defaultValue;
        initialValues.sources = defaultValue;
    } else {
        initialValues.period = [{ title: t?.dataTable?.all || 'All', value: defaultValue }];
        initialValues.currency = defaultCurrency;
    }

    const [selected, setSelected] = useState(getSelected(fields, initialValues));
    const [values, setValues] = useState(initialValues);

    const staticNames = t?.dataTable || {};

    const messagesForSelect = {
        emptyList: staticNames.emptyList || 'No items',
    };

    useEffect(() => {
        if (onAction) {
            onAction(values, 'firstRender');
        }
    }, [onAction, values]);

    const getValue = useCallback((data) => {
        if (data.name === 'datepicker') {
            return {
                startDate: values[`${data.key}StartDate`] || moment().subtract(config.defaultDatepickerDaysPeriod, 'days'),
                endDate: values[`${data.key}EndDate`] || moment().endOf('day'),
            };
        } else if (data.name === 'textInput') {
            return values[data.key] || '';
        }
        return values[data.key];
    }, [values]);

    const getNull = useCallback(() => null, []);

    const textInputChange = useCallback(debounce((name) => {
        const v = values[name];
        const len = typeof v === 'string' ? v.length : 0;
        if (len > 0 && len <= minLength) {
            const message = t?.validate?.minLineLength?.replace(/%s/i, minLength) || `Minimum length is ${minLength}`;
            CustomNotify(message, 'error');
        }
    }, 500), [values, t]);

    const deleteValue = useCallback((values, item) => {
        if (item.name === 'datepicker') {
            delete values[`${item.key}StartDate`];
            delete values[`${item.key}EndDate`];
        } else {
            delete values[item.key];
        }
    }, []);

    const onDataPickerApply = useCallback((datepicker, key) => {
        const newValues = {
            ...values,
            [`${key}StartDate`]: datepicker.startDate.clone().startOf('day'),
            [`${key}EndDate`]: datepicker.endDate.clone().endOf('day'),
        };
        setValues(newValues);
        if (onAction) {
            onAction(newValues);
        }
    }, [values, onAction]);

    const onTextInputChange = useCallback((event) => {
        const name = event.target.name;
        const newValues = { ...values, [name]: event.target.value };
        setValues(newValues);
        textInputChange(name);
    }, [values, textInputChange]);

    const onSelectChange = useCallback((event) => {
        const name = event.target.name;
        const newValues = { ...values, [name]: event.target.value };
        setValues(newValues);
        if (onAction) {
            onAction(newValues);
        }
    }, [values, onAction]);

    const onChange = useCallback((value, key) => {
        let val = Array.isArray(value) && value.length > 1
            ? value.filter((item) => item.value !== 'all')
            : value;

        const pick = (v) => (v && typeof v === 'object' && v.value !== undefined ? v.value : v);

        if (key === 'period') {
            const selected = Array.isArray(val) ? val[0] : val;
            const code = pick(selected);

            let nextStart = null;
            let nextEnd = null;

            const now = moment();
            switch (code) {
                case 'today':
                    nextStart = now.clone().startOf('day');
                    nextEnd = now.clone().endOf('day');
                    break;
                case 'yesterday':
                    nextStart = now.clone().subtract(1, 'day').startOf('day');
                    nextEnd = now.clone().subtract(1, 'day').endOf('day');
                    break;
                case 'last7':
                    nextStart = now.clone().subtract(7, 'day').startOf('day');
                    nextEnd = now.clone().endOf('day');
                    break;
                case 'last30':
                    nextStart = now.clone().subtract(30, 'day').startOf('day');
                    nextEnd = now.clone().endOf('day');
                    break;
                case 'thisMonth':
                    nextStart = now.clone().startOf('month');
                    nextEnd = now.clone().endOf('day');
                    break;
                case 'lastMonth':
                    nextStart = now.clone().subtract(1, 'month').startOf('month');
                    nextEnd = now.clone().subtract(1, 'month').endOf('month');
                    break;
                case 'all':
                default:
                    break;
            }

            const next = { ...values, period: selected };
            if (nextStart && nextEnd) {
                next.createStartDate = nextStart;
                next.createEndDate = nextEnd;
            } else {
                delete next.createStartDate;
                delete next.createEndDate;
            }
            setValues(next);
            if (onAction) onAction(next);
            return;
        }

        if (key === 'currency' && val && typeof val === 'object') {
            val = val.value;
        }

        const newValues = { ...values, [key]: val };
        setValues(newValues);
        if (onAction) onAction(newValues);
    }, [values, onAction]);

    const onChangeDynamic = useCallback((value, key) => {
        const newValue = value.map((item) => ({ title: item.title, value: item.value }));
        const newValues = { ...values, [key]: newValue };
        setValues(newValues);
        if (onAction) {
            onAction(newValues);
        }
    }, [values, onAction]);

    // handleAction removed (unused)

    const onChangeFilter = useCallback((item, index) => {
        const fieldIndex = fields.findIndex((data) => data.key === item.key);
        const newSelected = [...selected];
        const newValues = { ...values };
        const oldItem = newSelected[index];
        deleteValue(newValues, oldItem);
        newSelected[index] = { ...item, fieldIndex };
        setSelected(newSelected);
        setValues(newValues);
    }, [fields, selected, values, deleteValue]);

    const onAddFilter = useCallback((item) => {
        const fieldIndex = fields.findIndex((data) => data.key === item.key);
        setSelected(prev => {
            const next = [...prev];
            next.push({ ...item, fieldIndex });
            return next;
        });
    }, [fields]);

    const onDelete = useCallback((item, index) => {
        const newSelected = [...selected];
        const newValues = { ...values };
        const deletedItem = newSelected.find(({ name }) => name === item.name);
        const idx = index === undefined ? newSelected.indexOf(deletedItem) : index;
        if (idx < 0) return;
        deleteValue(newValues, deletedItem);
        newSelected.splice(idx, 1);
        setSelected(newSelected);
        setValues(newValues);
    }, [selected, values, deleteValue]);

    const getDatepicker = useCallback((data) => {
        const onDataPickerApplyHandler = (item) => onDataPickerApply(item, data.key);
        return (
            <DatePicker
                id={`finance-datepicker-${data.key}`}
                onApply={onDataPickerApplyHandler}
                className="btn form-control reactdaypicker-block"
                {...getValue(data)}
            />
        );
    }, [onDataPickerApply, getValue]);

    const getSelect = useCallback((select) => {
        const data = Array.isArray(fields[select.fieldIndex]?.data) ? fields[select.fieldIndex].data : [];
        return (
            <select
                id={`finance-select-${select.key}`}
                onChange={onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={getValue(select)}
            >
                {data.map((option, i) => (
                    <option key={i} value={option.value}>
                        {option.title}
                    </option>
                ))}
            </select>
        );
    }, [onSelectChange, getValue, fields]);

    const getDropdown = useCallback((dropdown) => {
        return (
            <DropdownList
                id={`finance-dropdown-${dropdown.key}`}
                messages={messagesForSelect}
                className="rw-dropdownlist-select"
                dataKey="value"
                textField="title"
                placeholder={dropdown.placeholder || ''}
                data={Array.isArray(fields[dropdown.fieldIndex]?.data) ? fields[dropdown.fieldIndex].data : []}
                value={getValue(dropdown)}
                onChange={(value) => onChange(value, dropdown.key)}
            />
        );
    }, [messagesForSelect, fields, getValue, onChange]);

    const getMultiselect = useCallback((select) => {
        return (
            <Multiselect
                id={`finance-multiselect-${select.key}`}
                messages={messagesForSelect}
                placeholder={select.placeholder || ''}
                className="right"
                dataKey="value"
                textField="title"
                onChange={(value) => onChange(value, select.key)}
                data={Array.isArray(fields[select.fieldIndex]?.data) ? fields[select.fieldIndex].data : []}
                name={select.key}
                value={Array.isArray(getValue(select)) ? getValue(select) : []}
            />
        );
    }, [messagesForSelect, fields, getValue, onChange]);

    const getTextInput = useCallback((data) => {
        return (
            <input
                id={`finance-textinput-${data.key}`}
                className="form-control"
                type="text"
                name={data.key}
                onChange={onTextInputChange}
                placeholder={data.placeholder}
                value={getValue(data)}
            />
        );
    }, [onTextInputChange, getValue]);

    const getCustom = useCallback((data) => {
        const onChangeCustom = (item) => onChange(item, data.key);
        return <Custom segment={data.label} values={getValue(data)} onAction={onChangeCustom} />;
    }, [onChange, getValue]);

    const getDynamic = useCallback((data) => {
        const onChangeDynamicHandler = (item) => onChangeDynamic(item, data.key);
        return (
            <DynamicSelectSegment
                segment={data.segment}
                multiselect={true}
                values={getValue(data)}
                onAction={onChangeDynamicHandler}
            />
        );
    }, [onChangeDynamic, getValue]);

    const getSelectGoalTree = useCallback((data) => {
        const onChangeHandler = (item) => onChange(item, data.key);
        return <InputGroupMultiselect nesting={data.nesting} onChange={onChangeHandler} tags={getValue(data)} />;
    }, [onChange, getValue]);

    const getChangeFunction = useCallback((value, key, name) => {
        if (name === 'currency' || key === 'currency') {
            return onChange(value, key);
        } else if (name === 'datepicker') {
            return onDataPickerApply(value, key);
        } else {
            alert('No function assigned');
        }
    }, [onChange, onDataPickerApply]);

    const renderFilter = useCallback((item = {}, i) => {
        const onChangeFilterHandler = (it) => onChangeFilter(it, i);

        const getFieldId = (item) => {
            if (!item.name || !item.key) return '';
            switch (item.name) {
                case 'datepicker': return `finance-datepicker-${item.key}`;
                case 'select': return `finance-select-${item.key}`;
                case 'dropdown': return `finance-dropdown-${item.key}`;
                case 'multiselect': return `finance-multiselect-${item.key}`;
                case 'textInput': return `finance-textinput-${item.key}`;
                default: return '';
            }
        };

        return (
            <div key={item.key || 'new-filter'}>
                {item.name ? (
                    <>
                        <div className="table-filter__item">
                            {item.label && (
                                <label htmlFor={getFieldId(item)}>
                                    {item.label}
                                    {item.name !== 'datepicker' && item.name !== 'dropdown' ? <i className="fa fa-caret-down" /> : null}
                                </label>
                            )}
                            {templateKeys[item.name] === 'getDatepicker' && getDatepicker(item)}
                            {templateKeys[item.name] === 'getSelect' && getSelect(item)}
                            {templateKeys[item.name] === 'getMultiselect' && getMultiselect(item)}
                            {templateKeys[item.name] === 'getDropdown' && getDropdown(item)}
                            {templateKeys[item.name] === 'getTextInput' && getTextInput(item)}
                            {templateKeys[item.name] === 'getCustom' && getCustom(item)}
                            {templateKeys[item.name] === 'getDynamic' && getDynamic(item)}
                            {templateKeys[item.name] === 'getSelectGoalTree' && getSelectGoalTree(item)}
                            {templateKeys[item.name] === 'getNull' && getNull()}
                        </div>
                    </>
                ) : null}
                {item.withList ? (
                    <ButtonsList
                        fields={fields}
                        data={item}
                        onChange={(value, key) => getChangeFunction(value, key, item.name)}
                        currentValue={values[item.key]}
                        showSubtitle={item.showSubtitle}
                        onChangeFilter={(it) => onChangeFilter(it, i)}
                    />
                ) : null}
            </div>
        );
    }, [onChangeFilter, onDelete, getDatepicker, getSelect, getMultiselect, getDropdown, getTextInput, getCustom, getDynamic, getSelectGoalTree, getNull, getChangeFunction, fields, values]);

    if (!fields.length) return null;

    return (
        <div className="finance-filter">
            <div className="table-filter">
                {selected.map((filter) => renderFilter(filter))}
                {fields.length !== selected.length ? renderFilter() : null}
            </div>
        </div>
    );
};

DataComponentFilterFinanceBlock.propTypes = {
    fields: PropTypes.array,
    onAction: PropTypes.func,
    startDate: PropTypes.object,
    endDate: PropTypes.object
};

DataComponentFilterFinanceBlock.defaultProps = {
    fields: [],
    onAction: () => {},
    startDate: null,
    endDate: null
};

export default DataComponentFilterFinanceBlock;
